import useGetInternalWidgetConfig from '@hooks/api/queries/widgets/useGetInternalWidgetConfig';
import useSubscription from '@hooks/useSubscription';
import Resistor from '@src/sld/components/Resistor/Resistor';
import { DynamicConnector } from '@src/sld/components/Shared';
import storeConnector from '@src/store/storeConnector';
import { DataPointWithValue } from '@src/types/DataPoint';
import { SiteMeta } from '@src/types/SiteMeta';
import { WidgetTypeDeviceList } from '@src/types/Widget';
import { ReactElement } from 'react';
import ArrowUp from '../components/ArrowUp/ArrowUp';
import BreakerBasic from '../components/Breaker/BreakerBasic';
import VerticalLine from '../components/Line/VerticalLine';
import Switch from '../components/Switch/Switch';
import { Superstition_Device_Points_Config } from './superstitionDevicePointsConfig';

type SuperstitionSLDProps = {
  siteMeta: SiteMeta;
};

const getDataPointsByPointNames = (
  dataPoints: DataPointWithValue[],
  pointNames: string[],
  engineeringId?: string,
): DataPointWithValue[] => {
  return dataPoints.filter(
    (point) =>
      pointNames.includes(point.pointName || '') &&
      (!engineeringId || point.sourceDeviceId === engineeringId),
  );
};

function SuperstitionDiagram(props: SuperstitionSLDProps): ReactElement {
  const { siteMeta } = props;

  const { data: rtacWidgetConfig } =
    useGetInternalWidgetConfig<WidgetTypeDeviceList>(
      'superstition_sld_rtac',
      {
        sourceDeviceId: siteMeta.siteId,
      },
      { enabled: true },
    );

  const rtacDataPointsWithValue = useSubscription(
    rtacWidgetConfig?.dataPoints || [],
  );

  let _891DataPoints: DataPointWithValue[] | undefined;
  let _52h1DataPoints: DataPointWithValue[] | undefined;
  let _52f1DataPoints: DataPointWithValue[] | undefined;
  let _52f2DataPoints: DataPointWithValue[] | undefined;

  if (rtacDataPointsWithValue.length > 0) {
    _891DataPoints = getDataPointsByPointNames(
      rtacDataPointsWithValue,
      Superstition_Device_Points_Config.STATUS_891_DATA_POINTS.map(
        (point) => point.pointName,
      ),
      Superstition_Device_Points_Config.STATUS_891_DATA_POINTS[0].engineeringId,
    );

    _52h1DataPoints = getDataPointsByPointNames(
      rtacDataPointsWithValue,
      Superstition_Device_Points_Config.STATUS_52H1_DATA_POINTS.map(
        (point) => point.pointName,
      ),
      Superstition_Device_Points_Config.STATUS_52H1_DATA_POINTS[0]
        .engineeringId,
    );

    _52f1DataPoints = getDataPointsByPointNames(
      rtacDataPointsWithValue,
      Superstition_Device_Points_Config.STATUS_52F1_DATA_POINTS.map(
        (point) => point.pointName,
      ),
      Superstition_Device_Points_Config.STATUS_52F1_DATA_POINTS[0]
        .engineeringId,
    );

    _52f2DataPoints = getDataPointsByPointNames(
      rtacDataPointsWithValue,
      Superstition_Device_Points_Config.STATUS_52F2_DATA_POINTS.map(
        (point) => point.pointName,
      ),
      Superstition_Device_Points_Config.STATUS_52F2_DATA_POINTS[0]
        .engineeringId,
    );
  }

  const adjustOpenOrCloseSwitchColor = (
    switchValue: string | number | null | undefined,
  ): string => (switchValue !== 0 ? 'red' : 'green');

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      xlinkHref='http://www.w3.org/1999/xlink'
      viewBox='125 70 1300 645'
      xmlSpace='preserve'
      colorInterpolationFilters='sRGB'
      className='st10'
      style={{ fill: 'none', stroke: 'currentColor' }}
    >
      <g>
        <title>Superstition</title>

        {/* top arrow and vertical line */}
        <ArrowUp xCoordinate={545} yCoordinate={98} />

        <VerticalLine
          translateXCoordinate={547}
          translateYCoordinate={98}
          length={50}
          color='white'
          width={1}
        />

        <g transform='translate(247 -52)'>
          <Switch
            isClosed={_891DataPoints?.[0]?.value === 1 ? true : false}
            translateXCoordinate={300}
            translateYCoordinate={200}
            label={'89-1'}
            stroke={adjustOpenOrCloseSwitchColor(_891DataPoints?.[0]?.value)}
            overrideLabelXCoordinate={20}
          />
        </g>

        <VerticalLine
          translateXCoordinate={547}
          translateYCoordinate={160}
          length={25}
          color='white'
          width={1}
        />

        <g transform='rotate(90 525 797.225)'>
          <Resistor label={'XFMR T1'} />
        </g>

        <VerticalLine
          translateXCoordinate={548}
          translateYCoordinate={202}
          length={75}
          color='white'
          width={1}
        />

        <BreakerBasic
          labels={[{ label: '52-H1', xCoordinate: 50, yCoordinate: 10 }]}
          translateXCoordinate={535}
          translateYCoordinate={185}
          dataPointsWithValues={_52h1DataPoints}
        />

        <VerticalLine
          translateXCoordinate={547.5}
          translateYCoordinate={298}
          length={25}
          color='white'
          width={1}
        />

        <BreakerBasic
          labels={[{ label: '52-F1', xCoordinate: 50, yCoordinate: 10 }]}
          translateXCoordinate={740}
          translateYCoordinate={415}
          dataPointsWithValues={_52f1DataPoints}
        />

        <BreakerBasic
          labels={[{ label: '52-F2', xCoordinate: 50, yCoordinate: 10 }]}
          translateXCoordinate={740}
          translateYCoordinate={520}
          dataPointsWithValues={_52f2DataPoints}
        />

        <g transform='translate(768.198 -362.583)'>
          <polygon
            transform='translate(146, 788) rotate(-0 0 0) scale(1.5)'
            points='0,5 0,0 5,2.5'
            stroke='white'
            fill='white'
          />

          <DynamicConnector
            label='34.5KV BESS FEEDER #1'
            pathCoordinates='M0 792h143.64'
            xCoordinate={248}
            yCoordinate={793}
            className='st7'
          />
        </g>

        <g transform='translate(837 -315)'>
          <polygon
            transform='translate(78, 788) rotate(-0 0 0) scale(1.5)'
            points='0,5 0,0 5,2.5'
            stroke='white'
            fill='white'
          />

          <DynamicConnector
            label='34.5KV BESS FEEDER #2'
            pathCoordinates='M0 744.42V792h75.28'
            xCoordinate={180}
            yCoordinate={795}
            className='st7'
          />
        </g>

        <g transform='translate(768.198 -257.477)'>
          <polygon
            transform='translate(146, 788) rotate(-0 0 0) scale(1.5)'
            points='0,5 0,0 5,2.5'
            stroke='white'
            fill='white'
          />

          <DynamicConnector
            label='34.5KV BESS FEEDER #3'
            pathCoordinates='M0 792h143.64'
            xCoordinate={250}
            yCoordinate={795}
            className='st7'
          />
        </g>

        <g transform='translate(837 -209.812)'>
          <polygon
            transform='translate(78, 788) rotate(-0 0 0) scale(1.5)'
            points='0,5 0,0 5,2.5'
            stroke='white'
            fill='white'
          />

          <DynamicConnector
            label='34.5KV BESS FEEDER #4'
            pathCoordinates='M0 744.42V792h75.28'
            xCoordinate={183}
            yCoordinate={797}
            className='st8'
          />
        </g>

        {/* top horizontal line */}
        <g transform='translate(741.125 -362.583)'>
          <path d='M0 792h-193.64v-95.41' className='st4' />
        </g>

        {/* bottom horizontal line */}
        <g transform='translate(547.489 -457.99)'>
          <path d='M0 780v212.51h193.64' className='st4' />
        </g>
      </g>
    </svg>
  );
}

export default storeConnector(SuperstitionDiagram, {
  config: ['siteMeta'],
});
